import { useMemo, useState } from 'react';
import { Banner, BlockStack, Box, Button, DropZone, Link, Text, TextField } from '@shopify/polaris';
import getSharePointUrlForFirnrDocumentTypeIdDocName from '../../../utils/sharepoint';
import { useLookupStore } from '../../../context/useStore.js';

// rendered inside the tabbed side card of the purchase order page
export default function PurchaseConfirmationPanel({ purchaseId, purchase, company, onPurchaseUpdated, onFieldChange, fieldDisabled = false, uploadDisabled = false }) {
  const documenttypes = useLookupStore((state) => state.documenttypes);
  const [uploading, setUploading] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const confirmationUrl = useMemo(() => {
    if (!purchase?.SupplierConfirmationFilename || !company?.Klantnummer || !purchase?.Bestellingnummer || !documenttypes?.length) return null;
    return getSharePointUrlForFirnrDocumentTypeIdDocName(company.Klantnummer, 13, purchase.Bestellingnummer, purchase.SupplierConfirmationFilename, documenttypes);
  }, [company?.Klantnummer, documenttypes, purchase?.Bestellingnummer, purchase?.SupplierConfirmationFilename]);

  const confirmedDeliveryDate = purchase?.SupplierConfirmedDeliveryDate ? String(purchase.SupplierConfirmedDeliveryDate).substring(0, 10) : '';
  const uploadedAt = purchase?.SupplierConfirmationUploadedAt ? new Date(purchase.SupplierConfirmationUploadedAt).toLocaleString() : '';

  const handleDropZoneDrop = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setUploading(true);
    setErrorMessage('');
    setWarnings([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/purchases/${purchaseId}/supplier-confirmation`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.msg || 'Failed to upload supplier confirmation.');
      }

      setParseResult(data?.parseResult || null);
      setWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
      if (data?.purchase) onPurchaseUpdated(data.purchase);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to upload supplier confirmation.');
    } finally {
      setUploading(false);
    }
  };

  if (purchaseId === 'new') {
    return <Text tone="subdued">Upload becomes available once the purchase order has been saved.</Text>;
  }

  return (
    <BlockStack gap="300">
      <TextField
        id="SupplierConfirmedDeliveryDate"
        type="date"
        autoComplete="off"
        label="Confirmed delivery date"
        value={confirmedDeliveryDate}
        onChange={(value) => onFieldChange(value, 'SupplierConfirmedDeliveryDate')}
        disabled={fieldDisabled}
      />

      {purchase?.SupplierConfirmationFilename ? (
        <Box>
          <Text as="p" variant="bodyMd">
            Current confirmation:{' '}
            {confirmationUrl ? (
              <Link removeUnderline url={confirmationUrl} target="_blank">
                {purchase.SupplierConfirmationFilename}
              </Link>
            ) : (
              purchase.SupplierConfirmationFilename
            )}
          </Text>
          {uploadedAt ? (
            <Box paddingBlockStart="100">
              <Text as="p" tone="subdued">
                Uploaded: {uploadedAt}
              </Text>
            </Box>
          ) : null}
        </Box>
      ) : (
        <Text tone="subdued">No supplier confirmation PDF uploaded yet.</Text>
      )}

      {parseResult ? (
        <Banner tone={parseResult.confirmedDeliveryDate ? 'success' : 'warning'} title={parseResult.confirmedDeliveryDate ? 'Confirmation parsed' : 'No date parsed'}>
          <p>{parseResult.notes}</p>
        </Banner>
      ) : null}

      {warnings.length > 0 ? (
        <Banner tone="warning" title="Upload warning">
          {warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </Banner>
      ) : null}

      {errorMessage ? (
        <Banner tone="critical" title="Upload failed">
          <p>{errorMessage}</p>
        </Banner>
      ) : null}

      <DropZone allowMultiple={false} accept=".pdf,application/pdf" onDrop={handleDropZoneDrop} disabled={uploading || uploadDisabled}>
        <DropZone.FileUpload
          actionTitle={uploading ? 'Uploading confirmation...' : 'Upload confirmation PDF'}
          actionHint="The PDF will be parsed automatically to detect the confirmed delivery date."
        />
      </DropZone>

      {confirmationUrl ? (
        <div>
          <Button onClick={() => window.open(confirmationUrl, '_blank')}>Open confirmation PDF</Button>
        </div>
      ) : null}
    </BlockStack>
  );
}
